import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  selectAuth,
  selectUser,
  selectIsAuthenticated,
  selectIsLoading,
  selectAuthError,
  selectTokens,
  selectIsProfileLoading,
  selectProfileError,
  loginUser,
  adminLoginStep1,
  adminLoginVerify,
  registerUser,
  verifyAccountCode,
  resendAccountCode,
  logoutUser,
  fetchUserProfile,
  updateUserProfile,
  fetchUserMe,
  addUserRole,
  switchUserRole,
  removeUserRole,
  clearError,
  setSession,
} from '../store/slices/authSlice';

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Selectors
  const auth = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectAuthError);
  const tokens = useSelector(selectTokens);
  const isProfileLoading = useSelector(selectIsProfileLoading);
  const profileError = useSelector(selectProfileError);

  // Actions
  const extractErrorMessage = (err) => {
    if (!err) return 'An error occurred';
    if (typeof err === 'string') return err;
    if (err.detail) return err.detail;
    if (err.message) return err.message;
    if (Array.isArray(err.non_field_errors) && err.non_field_errors.length > 0) {
      return err.non_field_errors[0];
    }
    // Find first field error
    for (const key in err) {
      const value = err[key];
      if (Array.isArray(value) && value.length > 0) return `${key}: ${value[0]}`;
      if (typeof value === 'string') return `${key}: ${value}`;
    }
    return 'An error occurred';
  };

  const login = async (credentials) => {
    try {
      // Land the user wherever their active_role last was (guest or a host
      // role) instead of forcing everyone back to guest on every login --
      // loginUser's reducer already persists whatever active_role the
      // backend reports, and HomePage/RoleGuard read that to route
      // accordingly, so no extra role-switch call is needed here.
      const result = await dispatch(loginUser(credentials)).unwrap();
      toast.success('Login successful!');
      return result;
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Login failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Step 1 of admin login: email+password -> emails an OTP. No toast here,
  // the admin login page shows its own "check your email" state.
  const adminLogin = async (credentials) => {
    try {
      return await dispatch(adminLoginStep1(credentials)).unwrap();
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Login failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Step 2: email+OTP -> real tokens, same auth state as a regular login.
  const verifyAdminLogin = async ({ email, code }) => {
    try {
      const result = await dispatch(adminLoginVerify({ email, code })).unwrap();
      toast.success('Login successful!');
      return result;
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Verification failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const result = await dispatch(registerUser(userData)).unwrap();
      toast.success('Registration successful! Please enter the verification code we sent you.');
      return result;
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Registration failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const verifyCode = async ({ email, code }) => {
    try {
      const result = await dispatch(verifyAccountCode({ email, code })).unwrap();
      toast.success('Account verified successfully!');
      return result;
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Verification failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const resendCode = async ({ email }) => {
    try {
      const result = await dispatch(resendAccountCode({ email })).unwrap();
      toast.success('A new code has been sent.');
      return result;
    } catch (error) {
      const errorMessage = extractErrorMessage(error) || 'Failed to resend code';
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast.success('Logout successful!');
      navigate('/');
    } catch (error) {
      // Even if logout fails, redirect to home
      toast.error('Logout failed, but you have been signed out locally.');
      navigate('/');
    }
  };

  const getProfile = async () => {
    try {
      const result = await dispatch(fetchUserProfile()).unwrap();
      return result;
    } catch (error) {
      const errorMessage = error?.detail || error?.message || 'Failed to fetch profile';
      toast.error(errorMessage);
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const result = await dispatch(updateUserProfile(profileData)).unwrap();
      toast.success('Profile updated successfully!');
      return result;
    } catch (error) {
      const errorMessage = error?.detail || error?.message || 'Failed to update profile';
      toast.error(errorMessage);
      throw error;
    }
  };

  const clearAuthError = () => {
    dispatch(clearError());
  };

  // Establishes a session from tokens/user data the backend already
  // returned (e.g. the become-host wizard creating an account inline) --
  // no extra login round-trip needed.
  const establishSession = ({ user: sessionUser, tokens: sessionTokens }) => {
    if (sessionTokens?.access) localStorage.setItem('accessToken', sessionTokens.access);
    if (sessionTokens?.refresh) localStorage.setItem('refreshToken', sessionTokens.refresh);
    dispatch(setSession({ user: sessionUser, tokens: sessionTokens }));
  };

  const getMe = async () => {
    try {
      const result = await dispatch(fetchUserMe()).unwrap();
      return result;
    } catch (error) {
      const errorMessage = error?.detail || error?.message || 'Failed to fetch user details';
      toast.error(errorMessage);
      throw error;
    }
  };

  const addRole = async (roleData) => {
    try {
      const result = await dispatch(addUserRole(roleData)).unwrap();
      toast.success('Role added successfully!');
      return result;
    } catch (error) {
      const errorMessage = error?.detail || error?.message || 'Failed to add role';
      toast.error(errorMessage);
      throw error;
    }
  };

  const switchRole = async (roleData) => {
    try {
      const result = await dispatch(switchUserRole(roleData)).unwrap();
      toast.success(`Switched to ${roleData.role} role!`);
      return result;
    } catch (error) {
      const errorMessage = error?.detail || error?.message || 'Failed to switch role';
      toast.error(errorMessage);
      throw error;
    }
  };

  const removeRole = async (role) => {
    try {
      const result = await dispatch(removeUserRole(role)).unwrap();
      toast.success('Role removed successfully!');
      return result;
    } catch (error) {
      const errorMessage = error?.detail || error?.message || 'Failed to remove role';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Check if user has any host role
  const isHost = user?.roles?.some(role => ['bnb_host', 'hotel_host'].includes(role));
  
  // Check if user is a guest
  const isGuest = user?.active_role === 'guest' || user?.roles?.includes('guest');
  
  // Check specific roles based on active role
  const isBnBHost = user?.active_role === 'bnb_host';
  const isHotelRestaurantHost = user?.active_role === 'hotel_host';
  
  // Check if user is verified
  const isVerified = user?.is_verified;
  
  // Check if user is premium
  const isPremium = user?.is_premium;

  // Super admin dashboard tier -- based on admin_tier, the same field
  // AdminRoleGuard checks, not active_role (which the host flags above use
  // and which doesn't apply to admin accounts).
  const isSystemAdmin = user?.admin_tier === 'system';
  const isManagerialAdmin = user?.admin_tier === 'managerial';
  const isSupportAdmin = user?.admin_tier === 'support';
  const isAnyAdmin = Boolean(user?.admin_tier);

  return {
    // State
    auth,
    user,
    isAuthenticated,
    isLoading,
    error,
    tokens,
    isProfileLoading,
    profileError,

    // Actions
    login,
    adminLogin,
    verifyAdminLogin,
    register,
    verifyCode,
    resendCode,
    logout,
    getProfile,
    updateProfile,
    getMe,
    addRole,
    switchRole,
    removeRole,
    clearAuthError,
    establishSession,

    // Computed values
    isHost,
    isGuest,
    isBnBHost,
    isHotelRestaurantHost,
    isVerified,
    isPremium,
    isSystemAdmin,
    isManagerialAdmin,
    isSupportAdmin,
    isAnyAdmin,
  };
};
